import type { FC } from 'react';
import { Box, HStack, Stack, Text, VStack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { Profile, Transaction } from 'types';

import { fixDecimal } from 'utils';

import TransactionMethod from '../TransactionMethod';
import TransactionStatus from '../TransactionStatus';

export type TransactionListItemProps = {
  transaction: Transaction;
  profile: Profile | undefined | null;
  onClick: () => void;
};

const TransactionListItem: FC<TransactionListItemProps> = ({
  transaction,
  profile,
  onClick,
}) => {
  const trType = transaction.transaction_method.type;
  const isDestination =
    typeof transaction.destination_wallet === 'object' &&
    transaction.destination_wallet !== null;
  const isCrypto = ['DEPOSIT_CRYPTO', 'WITHDRAW_CRYPTO'].includes(
    transaction.transaction_method.category
  );
  const swapDetail =
    trType === 'SWAP'
      ? {
          destination_amount: fixDecimal({
            value: transaction.destination_amount,
            fractionDigits: isCrypto ? 5 : 2,
          }),
          destination_symbol: transaction?.destination_wallet?.currency?.symbol,
          source_amount: fixDecimal({
            value: transaction.amount,
            fractionDigits: isCrypto ? 5 : 2,
          }),
          source_symbol: transaction?.source_wallet?.currency?.symbol,
        }
      : {};

  const icon = isDestination
    ? transaction?.destination_wallet?.currency?.icon
    : transaction?.source_wallet?.currency?.icon;
  const symbol = isDestination
    ? transaction?.destination_wallet?.currency?.symbol
    : transaction?.source_wallet?.currency?.symbol;
  const title = isDestination
    ? transaction?.destination_wallet?.currency?.title
    : transaction?.source_wallet?.currency?.title;
  const amount = isDestination
    ? transaction.destination_amount
    : transaction.amount;
  const isLDTr = ['DEPOSIT_LD', 'WITHDRAW_LD'].includes(
    transaction.transaction_method.category
  );
  const isBrokerTr = ['DEPOSIT_BROKER', 'WITHDRAW_BROKER'].includes(
    transaction.transaction_method.category
  );

  const isIntermediary =
    (profile?.role.title === 'LD' && isLDTr) ||
    (profile?.role.title === 'BROKER' && isBrokerTr);

  const positiveOrNegative = {
    DEPOSIT: isIntermediary ? '-' : '+',
    WITHDRAW: isIntermediary ? '+' : '-',
    TRANSFER: isDestination ? '+' : '-',
    SWAP: isDestination ? '+' : '-',
  } as const;

  return (
    <HStack
      w="100%"
      align="center"
      gap={{ base: 3, md: 4 }}
      py={{ base: 4, md: 3 }}
      px={{ base: 0, md: 1 }}
      borderBottomWidth="1px"
      borderBottomColor="rgba(255,255,255,0.08)"
      cursor="pointer"
      transition="background 0.15s ease"
      _hover={{ bg: 'rgba(255, 255, 255, 0.04)' }}
      onClick={onClick}
    >
      <HStack flex="1" minW={0} align="center" gap={3}>
        <Box
          w="36px"
          h="36px"
          borderRadius="full"
          overflow="hidden"
          flexShrink={0}
        >
          <img src={icon} alt={symbol} width="36" height="36" />
        </Box>
        <VStack align="flex-start" gap={0} minW={0}>
          <Text
            fontSize="var(--chakra-font-sizes-sm)"
            color="body"
            fontWeight="medium"
            lineHeight="short"
          >
            {title}
          </Text>
          <Text
            fontSize="var(--chakra-font-sizes-sm)"
            color="body2"
            lineHeight="short"
          >
            {dayjs(transaction.created_at).format('YYYY-MM-DD HH:mm:ss')}
          </Text>
        </VStack>
      </HStack>
      <HStack
        align="center"
        gap={{ base: 2, md: 3 }}
        flexShrink={0}
        flexDir={{ base: 'column-reverse', sm: 'row' }}
      >
        <VStack align={{ base: 'flex-end', sm: 'flex-end' }} gap={0}>
          {trType !== 'SWAP' && (
            <Text
              fontSize="var(--chakra-font-sizes-sm)"
              fontWeight="medium"
              color="body"
              fontVariantNumeric="tabular-nums"
              textAlign="end"
            >
              {
                positiveOrNegative[
                  transaction.transaction_method
                    .type as keyof typeof positiveOrNegative
                ]
              }
              {`${fixDecimal({
                value: amount,
                fractionDigits: isCrypto ? 5 : 2,
              })} ${symbol}`}
            </Text>
          )}
          {trType === 'SWAP' && (
            <Stack gap={0} align="flex-end">
              <Text
                fontSize="var(--chakra-font-sizes-sm)"
                fontWeight="medium"
                color="body"
                fontVariantNumeric="tabular-nums"
              >
                {`+ ${swapDetail.destination_amount} ${swapDetail.destination_symbol}`}
              </Text>
              <Text
                fontSize="var(--chakra-font-sizes-sm)"
                color="body2"
                fontVariantNumeric="tabular-nums"
              >
                {`- ${swapDetail.source_amount} ${swapDetail.source_symbol}`}
              </Text>
            </Stack>
          )}
        </VStack>
        <HStack align="center" gap={2}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <TransactionMethod
              type={transaction.transaction_method.type}
              isIntermediary={isIntermediary}
            />
          </Box>
          <TransactionStatus status={transaction.status} />
        </HStack>
      </HStack>
    </HStack>
  );
};

export default TransactionListItem;
